import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'components/button';
import MessageText from 'components/message/MessageText';
import Message from 'components/message/Message';
import { Card, CardBody } from 'components/card';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from 'messages';
import {
  ROUTE_EXERCISES_COLLECTIONS_ENTITY,
  ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_EXERCISES_EXERCISE
} from 'routes/RouteList';
import Icon from 'components/icon/Icon';
import { useStore } from 'stores/Store';
import TemplateForm from 'containers/pages/exercises/templates/TemplateForm';
import TemplateExercisesListSortable from 'containers/pages/exercises/templates/TemplateExercisesListSortable';
import { sortByArray } from 'utils/sort';
import { TemplateType } from 'constants';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import { useClickAway } from '@uidotdev/usehooks';
import { MODAL_TYPES } from 'models/ModalData';
import { StoreContext } from 'index';
import Template from 'models/Template';
import CollectionForm from 'containers/pages/exercises/collections/CollectionForm';
import AddExercisesModal from 'containers/pages/exercises/exercises/AddExercisesModal';
import AddTrainingModal from 'containers/pages/exercises/trainings/AddTrainingModal';
import Training from 'models/Training';
import { cleanFalsy } from 'utils/object';
import { useNotificationQueue } from 'components/notification';
import useCollections from 'hooks/queries/useCollections';
import { useContextMenu } from 'contexts/ContextMenuProvider';
import RemoveExercisesFromTemplateButton from 'containers/partials/buttons/RemoveExercisesFromTemplateButton';
import useCollection from 'hooks/queries/useCollection';
import RemoveExerciseTemplateButton from 'containers/partials/buttons/RemoveExerciseTemplateButton';
import AddButton from 'containers/partials/buttons/AddButton';

const CollectionExercisesTab = () => {
  const { uiState } = useContext(StoreContext);
  const intl = useIntl();
  const { entityId, tab, templateId } = useParams();
  const { push } = useHistory();
  const ability = useAbility(AbilityContext);
  const notification = useNotificationQueue();
  const { addActionButton, setMenuItems, clearAllContext } = useContextMenu();

  const templateFormRef = useRef();
  const defaultSort = useRef([]);

  const { fetchCollection } = useCollection();
  const { fetchCollections } = useCollections(entityId);

  const collection = useStore(state => state.collection);
  const setCollection = useStore(state => state.setCollection);

  const setSelectedExercises = useStore(state => state.setSelectedExercises);
  const selectedExercises = useStore(state => state.selectedExercises);
  const selectExercises = useStore(state => state.selectExercises);
  const resetSelectedExercises = useStore(
    state => state.resetSelectedExercises
  );

  const [addExercisesModal, setAddExercisesModal] = useState(null);
  const templateSortableActive = useStore(
    state => state.templateSortableActive
  );
  const setTemplateSortableActive = useStore(
    state => state.setTemplateSortableActive
  );
  const [addModalData, setAddModalData] = useState(null);
  const sortableContainerRef = useClickAway(() => {
    if (templateSortableActive) {
      uiState.showModal({
        title: intl.formatMessage(
          messages.exercisesSortExercisesModalWarningTitle
        ),
        message: intl.formatMessage(
          messages.exercisesSortExercisesModalWarningMessage
        ),
        okLabel: intl.formatMessage(messages.saveButton),
        dismissLabel: intl.formatMessage(messages.buttonBack),
        type: MODAL_TYPES.WARNING,
        okHandler: () => {
          templateFormRef.current.submitForm();
        }
      });
    }
  });

  useEffect(() => {
    const contextButtons = [];

    if (ability.can('update', collection)) {
      contextButtons.push(<AddExercisesMenuBtn />);
    }
    contextButtons.push(<UseCollectionBtn />);

    if (ability.can('update', collection)) {
      contextButtons.push(<RemoveExercisesBtn />, <SortExercisesBtn />);
    }

    if (ability.can('delete', collection)) {
      contextButtons.push(<DeleteCollectionBtn />);
    }

    setMenuItems(contextButtons);

    if (ability.can('update', collection)) {
      addActionButton(<AddExercisesBtn />);
    }

    return () => {
      clearAllContext();
    };
  }, [collection, selectedExercises, templateSortableActive]);

  useEffect(() => {
    defaultSort.current = collection.exercises.map(exercise => exercise.id);
    setSelectedExercises([]);
    return () => {
      resetSelectedExercises();
    };
  }, [collection]);

  const onClickHandler = exerciseId => {
    push(
      generatePath(
        ROUTE_EXERCISES_COLLECTIONS_ENTITY_COLLECTION_EXERCISES_EXERCISE,
        {
          entityId,
          tab,
          templateId,
          exerciseId
        }
      )
    );
  };

  const onCancel = () => {
    setTemplateSortableActive(false);
    setCollection(
      new Template({
        ...collection,
        exercises: sortByArray(collection.exercises, defaultSort.current)
      })
    );
  };

  const onAddedHandler = () => {
    setAddExercisesModal(false);
  };

  const onCollectionDeleted = async templateId => {
    notification.add(`removed_${templateId}`, {
      message: intl.formatMessage(messages.messageRemovedcollectionSuccess)
    });
    await fetchCollections({
      variables: { entityId, type: TemplateType.COLLECTION },
      fetchPolicy: 'network-only'
    });
    push(
      generatePath(ROUTE_EXERCISES_COLLECTIONS_ENTITY, {
        entityId,
        tab
      })
    );
  };

  const createTrainingFromCollection = async collection => {
    const trainingObj = cleanFalsy({
      entityId,
      title: collection.title,
      exerciseIds: collection.exercises.map(e => e.id),
      type: TemplateType.TRAINING
    });

    setAddModalData(trainingObj);
  };

  const AddExercisesBtn = () => (
    <AddButton
      expandable
      label={<FormattedMessage {...messages.exercisesAddExercisesButton} />}
      onClick={() => setAddExercisesModal(collection)}
    />
  );

  const AddExercisesMenuBtn = () => (
    <Button menu onClick={() => setAddExercisesModal(collection)}>
      <Icon id="add" />
      <FormattedMessage {...messages.exercisesAddExercisesButton} />
    </Button>
  );

  const UseCollectionBtn = () => (
    <Button menu onClick={() => createTrainingFromCollection(collection)}>
      <Icon id="use-template" />
      <FormattedMessage {...messages.copyCollectionToTemplateButton} />
    </Button>
  );
  const RemoveExercisesBtn = () => (
    <RemoveExercisesFromTemplateButton
      disabledMessage={
        templateSortableActive
          ? intl.formatMessage(
              messages.collectionRemoveExerciseDisabledDragging
            )
          : intl.formatMessage(
              messages.collectionRemoveExerciseDisabledNotSelected
            )
      }
      entityId={entityId}
      type="collection"
      exerciseIds={selectedExercises.map(e => e.id)}
      templateId={templateId}
      updated={async () => {
        setSelectedExercises([]);
        await fetchCollection({
          variables: { templateId },
          fetchPolicy: 'network-only'
        });
        await fetchCollections({
          variables: { entityId, type: TemplateType.COLLECTION },
          fetchPolicy: 'network-only'
        });
      }}
      disabled={selectedExercises.length === 0}
    />
  );

  const SortExercisesBtn = () => (
    <Button
      menu
      onClick={() => setTemplateSortableActive(true)}
      disabled={selectedExercises.length > 0 || templateSortableActive}
    >
      {templateSortableActive ? (
        <FormattedMessage
          {...messages.collectionSortExercisesDisabledDragging}
        />
      ) : (
        <FormattedMessage {...messages.sortCollectionButton} />
      )}
      <Icon id="arrange" strokeColor="color-neutral-dark" />
    </Button>
  );

  const DeleteCollectionBtn = () => (
    <RemoveExerciseTemplateButton
      entityId={entityId}
      templateId={templateId}
      collectionType={TemplateType.COLLECTION}
      menu
      onDeleted={templateId => onCollectionDeleted(templateId)}
    >
      <FormattedMessage {...messages.removeCollectionButton} />
    </RemoveExerciseTemplateButton>
  );

  return (
    <>
      <div
        ref={sortableContainerRef}
        className="o-flex o-flex--column"
        style={{
          // flex: 1,
          flex: 'auto',
          marginBottom: templateSortableActive ? 72 : 0,
          overflowY: 'auto',
          overflowX: 'visible'
        }}
      >
        {collection.exercises.length > 0 ? (
          <TemplateForm
            ref={templateFormRef}
            entityId={entityId}
            template={collection}
            type={TemplateType.COLLECTION}
            onComplete={() => setTemplateSortableActive(false)}
          >
            <TemplateExercisesListSortable
              exercises={collection.exercises}
              checkable={!templateSortableActive}
              showNote={false}
              edit={!templateSortableActive}
              onClick={onClickHandler}
              onCancel={onCancel}
              selectedExercises={selectedExercises}
              onSelectedExercisesChange={selectExercises}
            />
          </TemplateForm>
        ) : (
          <Card centered>
            <CardBody empty>
              {ability.can('update', collection) ? (
                <Message emptyState={true} icon="browse">
                  {collection.exercises.length ? (
                    <MessageText>
                      <FormattedMessage
                        {...messages.exercisesListEmptyResult}
                      />
                    </MessageText>
                  ) : (
                    <MessageText>
                      <FormattedMessage {...messages.templateEmptyExercises} />
                    </MessageText>
                  )}
                  <Button
                    primary
                    rounded
                    onClick={() => setAddModalData(collection)}
                  >
                    <Icon id="add-white" />
                    <FormattedMessage
                      {...messages.exercisesAddExercisesButton}
                    />
                  </Button>
                </Message>
              ) : (
                <Message emptyState={true} icon="browse">
                  {collection.exercises.length ? (
                    <MessageText>
                      <FormattedMessage
                        {...messages.exercisesListEmptyResult}
                      />
                    </MessageText>
                  ) : (
                    <MessageText>
                      <FormattedMessage
                        {...messages.templateEmptyExercisesAndCantAdd}
                      />
                    </MessageText>
                  )}
                </Message>
              )}
            </CardBody>
          </Card>
        )}
      </div>
      {addModalData && (
        <AddTrainingModal
          entityId={entityId}
          training={new Training({ ...addModalData })}
          onClose={() => setAddModalData(false)}
        />
      )}
      {ability.can('update', collection) && addExercisesModal && (
        <CollectionForm
          entityId={entityId}
          template={collection}
          type={TemplateType.COLLECTION}
          onComplete={() => setAddExercisesModal(false)}
        >
          {() => (
            <AddExercisesModal
              entityId={entityId}
              template={collection}
              onClose={() => {
                setAddExercisesModal(false);
              }}
              onAdded={() => onAddedHandler()}
            />
          )}
        </CollectionForm>
      )}
    </>
  );
};

export default CollectionExercisesTab;
